// The session token lives in memory, mirrored to sessionStorage so a reload keeps the
// session but closing the tab ends it. Never localStorage. With a provider that issues
// them (Supabase), the refresh token is kept the same way, so the session keeper
// (./keeper.ts) can renew the access token before it expires (B-001).

const KEY = 'admin.session.token';
const REFRESH_KEY = 'admin.session.refresh';
let token: string | null | undefined;
let refreshToken: string | null | undefined;
const listeners = new Set<() => void>();

function storage(): Storage | null {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage;
  } catch {
    return null;
  }
}

function write(key: string, value: string | null) {
  const s = storage();
  if (value) s?.setItem(key, value);
  else s?.removeItem(key);
}

export function getToken(): string | null {
  if (token === undefined) token = storage()?.getItem(KEY) ?? null;
  return token;
}

export function getRefreshToken(): string | null {
  if (refreshToken === undefined) refreshToken = storage()?.getItem(REFRESH_KEY) ?? null;
  return refreshToken;
}

/** A signed-in session: the API bearer token, and a refresh token when the provider has one. */
export interface StoredSession {
  token: string;
  refreshToken?: string | null;
}

/** Stores a session (or clears it with `null`), then tells the listeners. */
export function setSession(next: StoredSession | null): void {
  token = next?.token ?? null;
  refreshToken = next?.refreshToken ?? null;
  write(KEY, token);
  write(REFRESH_KEY, refreshToken);
  listeners.forEach((l) => l());
}

/** Stores a bearer token without a refresh token (or signs out with `null`). */
export function setToken(next: string | null): void {
  setSession(next ? { token: next } : null);
}

export function onTokenChange(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * The `exp` and `iat` of a JWT, in milliseconds, read without verifying anything (the API
 * verifies; this only decides when to renew). Null when the token isn't a JWT with an `exp`.
 */
export function tokenTimes(jwt: string): { expiresAt: number; issuedAt: number | null } | null {
  const part = jwt.split('.')[1];
  if (!part) return null;
  try {
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    const payload = JSON.parse(json) as { exp?: unknown; iat?: unknown };
    if (typeof payload.exp !== 'number' || !Number.isFinite(payload.exp)) return null;
    return {
      expiresAt: payload.exp * 1000,
      issuedAt:
        typeof payload.iat === 'number' && Number.isFinite(payload.iat) ? payload.iat * 1000 : null,
    };
  } catch {
    return null;
  }
}

/** Test helper: forget the in-memory copies so the next read comes from storage. */
export function resetTokenCacheForTests(): void {
  token = undefined;
  refreshToken = undefined;
}
